import axios from 'axios'

const apiURL = process.env.REACT_APP_API_URL

/** Someone on the piggy bank screen, with what their piggy bank adds up to. */
export interface PiggyBankUser {
  employeeId: number
  employeeName: string
  /** The employee has been deleted; their piggy bank is still shown. */
  isDeleted: boolean
  referralCount: number
  referralTotal: number
  allocationCount: number
  allocationTotal: number
  /** Everything earned: referrals plus items. */
  total: number
  paymentCount: number
  paidTotal: number
  /** What they're still owed: earned less paid. */
  balance: number
  lastEntryDate: string | null
  lastPaidDate: string | null
}

export interface PiggyBankJob {
  jobId: number
  ref: string
  createdDate: string
}

/**
 * A referral that became a job, a catalogue item allocated from the portal, or
 * a payment out of the piggy bank.
 */
export interface PiggyBankEntry {
  type: 'Referral' | 'Allocation' | 'Payment'
  /** The referral, allocation or payment id, depending on type. */
  id: number
  date: string | null
  /** Always positive: a payment takes it out, everything else adds it. */
  value: number
  isPayment: boolean
  /** The referral's description, the allocated item's name, or "Payment". */
  title: string
  fromJobId: number | null
  fromJobRef: string | null
  fromJobClient: string | null
  enquiryId: number | null
  enquiryRef: string | null
  resultingJobs: PiggyBankJob[]
  piggyBankItemId: number | null
  itemDescription: string | null
  notes: string | null
  createdBy: string | null
}

export interface PiggyBankUserDetail {
  employeeId: number
  employeeName: string
  isDeleted: boolean
  /** Everything earned: referrals plus items. */
  total: number
  paidTotal: number
  /** What they're still owed: earned less paid. */
  balance: number
  entries: PiggyBankEntry[]
}

/** What went into piggy banks in one calendar month. */
export interface PiggyBankMonth {
  year: number
  /** 1-12. */
  month: number
  referralCount: number
  referralTotal: number
  allocationCount: number
  allocationTotal: number
  total: number
}

export interface PiggyBankItem {
  piggyBankItemId: number
  name: string
  description: string | null
  /** What an allocation of it starts at. */
  value: number
  /** How many times it has been given (and not taken back). */
  allocationCount: number
  createdDate: string
  createdBy: string | null
}

export interface PiggyBankItemInput {
  name: string
  description: string
  value: number
}

export interface PiggyBankAllocationInput {
  piggyBankItemId: number
  value: number
  notes: string
}

export interface PiggyBankPaymentInput {
  amount: number
  /** yyyy-MM-dd, the day it was paid. */
  paidDate: string
  notes: string
}

const headers = () => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  return {
    'Content-Type': 'application/json',
    Authorization: 'Bearer ' + token,
  }
}

const url = (path: string) => `https://${apiURL}/PiggyBank/${path}`

// --- Everyone's piggy banks ----------------------------------------------------

export const getPiggyBankUsers = async (): Promise<PiggyBankUser[]> =>
  (await axios.get(url('Users'), { headers: headers() })).data

export const getPiggyBankUser = async (employeeId: number): Promise<PiggyBankUserDetail> =>
  (await axios.get(url(`Users/${employeeId}`), { headers: headers() })).data

/** Returns the person's piggy bank with the new allocation in it. */
export const allocatePiggyBankItem = async (
  employeeId: number,
  allocation: PiggyBankAllocationInput
): Promise<PiggyBankUserDetail> =>
  (await axios.post(url(`Users/${employeeId}/Allocations`), allocation, { headers: headers() })).data

export const removePiggyBankAllocation = async (allocationId: number): Promise<void> => {
  await axios.delete(url(`Allocations/${allocationId}`), { headers: headers() })
}

/** Returns the person's piggy bank with the payment in it. */
export const recordPiggyBankPayment = async (
  employeeId: number,
  payment: PiggyBankPaymentInput
): Promise<PiggyBankUserDetail> =>
  (await axios.post(url(`Users/${employeeId}/Payments`), payment, { headers: headers() })).data

export const removePiggyBankPayment = async (paymentId: number): Promise<void> => {
  await axios.delete(url(`Payments/${paymentId}`), { headers: headers() })
}

/**
 * Month by month for the last `months` months up to this one, every month
 * listed. Everyone's, or one person's when employeeId is given.
 */
export const getPiggyBankMonthly = async (months: number, employeeId: number | null): Promise<PiggyBankMonth[]> => {
  const query = new URLSearchParams({ months: String(months) })
  if (employeeId !== null) query.set('employeeId', String(employeeId))
  return (await axios.get(url(`Monthly?${query.toString()}`), { headers: headers() })).data
}

// --- The catalogue ---------------------------------------------------------------

export const getPiggyBankItems = async (): Promise<PiggyBankItem[]> =>
  (await axios.get(url('Items'), { headers: headers() })).data

export const createPiggyBankItem = async (item: PiggyBankItemInput): Promise<PiggyBankItem> =>
  (await axios.post(url('Items'), item, { headers: headers() })).data

export const updatePiggyBankItem = async (itemId: number, item: PiggyBankItemInput): Promise<PiggyBankItem> =>
  (await axios.put(url(`Items/${itemId}`), item, { headers: headers() })).data

export const deletePiggyBankItem = async (itemId: number): Promise<void> => {
  await axios.delete(url(`Items/${itemId}`), { headers: headers() })
}
